import React, { useState, useRef, useEffect, useGlobal } from "reactn";
import axios from "axios";
import { SearchIcon } from "../../../assets";
import "./AIMagicReportModal.css";

const AIMagicReportModal = ({ isOpen, onClose }) => {
  const [partnerPathway, setPartnerPathway] = useGlobal("partnerPathway");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [currentFollowUpQuestions, setCurrentFollowUpQuestions] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    const rootCrumb =
      partnerPathway.length > 0
        ? partnerPathway[0]
        : { label: "Home", to: "/dashboard" };

    setPartnerPathway([
      rootCrumb,
      { label: "Reports & Analytics", to: "/reporting" },
      { label: "AI Magic Report", to: "/reports/ai-magic-report" }
    ]);

    loadChatHistory();
  }, []);

  const loadChatHistory = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/partners/ai-magic-report/history`
      );
      if (response.data.success) {
        setChatHistory(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading chat history:", error);
    }
  };

  const saveChatToHistory = async chatData => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/partners/ai-magic-report/save-chat`,
        chatData
      );
      if (response.data.success) {
        loadChatHistory();
      }
    } catch (error) {
      console.error("Error saving chat:", error);
    }
  };

  const generateChatTitle = conversation => {
    const firstUserMessage = conversation.find(msg => msg.type === "user");
    if (firstUserMessage) {
      return firstUserMessage.content.length > 40
        ? firstUserMessage.content.substring(0, 40) + "..."
        : firstUserMessage.content;
    }
    return "New Chat";
  };

  const startNewChat = async () => {
    if (conversationHistory.length > 0 && !currentChatId) {
      const chatData = {
        title: generateChatTitle(conversationHistory),
        messages: conversationHistory,
        createdAt: new Date()
      };
      await saveChatToHistory(chatData);
    }

    await loadChatHistory();

    setConversationHistory([]);
    setCurrentFollowUpQuestions([]);
    setCurrentChatId(null);
    setSearchQuery("");
    setShowSidebar(false);
  };

  const loadChatFromHistory = async chatId => {
    setIsLoadingHistory(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/partners/ai-magic-report/chat/${chatId}`
      );
      if (response.data.success) {
        const chat = response.data.data;
        setConversationHistory(chat.messages || []);
        setCurrentChatId(chatId);
        setCurrentFollowUpQuestions(chat.lastFollowUpQuestions || []);
        setShowSidebar(false);

        await loadChatHistory();
      }
    } catch (error) {
      console.error("Error loading chat:", error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const exportTableToCSV = tableData => {
    if (!tableData || !tableData.exportable) return;

    const csvContent = [
      tableData.headers.join(","),
      ...tableData.data.map(row =>
        row
          .map(cell => {
            const cellStr = String(cell || "");
            return cellStr.includes(",") ? `"${cellStr}"` : cellStr;
          })
          .join(",")
      )
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${tableData.title.replace(/\s+/g, "_")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderTable = tableData => {
    if (!tableData) return null;

    return (
      <div className="aimagic-table-container">
        <div className="aimagic-table-header">
          <h4 className="aimagic-table-title">{tableData.title}</h4>
          {tableData.exportable && (
            <button
              className="aimagic-export-btn"
              onClick={() => exportTableToCSV(tableData)}
              title="Export to CSV"
            >
              Export CSV
            </button>
          )}
        </div>

        <div className="aimagic-table-wrapper">
          <table className="aimagic-data-table">
            <thead>
              <tr>
                {tableData.headers.map((header, index) => (
                  <th key={index}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.data.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="aimagic-table-footer">
          <small>
            {tableData.data.length} rows • Generated{" "}
            {new Date().toLocaleString()}
          </small>
        </div>
      </div>
    );
  };

  const formatAIResponse = text => {
    let formatted = text.trim();

    formatted = formatted
      .replace(/^## (.+)$/gm, '<h3 class="ai-response-header">$1</h3>')
      .replace(/^### (.+)$/gm, '<h4 class="ai-response-subheader">$1</h4>')
      .replace(/^#\s*$/gm, "")

      .replace(/\*\*(.*?)\*\*/g, '<strong class="ai-response-bold">$1</strong>')

      .replace(/^[•\-] (.+)$/gm, '<div class="ai-response-bullet">$1</div>')

      .replace(/\n\n+/g, '</p><p class="ai-response-paragraph">')

      .replace(/\n/g, "<br/>");

    formatted = '<p class="ai-response-paragraph">' + formatted + "</p>";

    formatted = formatted
      .replace(/<p class="ai-response-paragraph"><\/p>/g, "")
      .replace(
        /<p class="ai-response-paragraph"><br\/>/g,
        '<p class="ai-response-paragraph">'
      )
      .replace(/<br\/><\/p>/g, "</p>")
      .replace(/<br\/>\s*<h3/g, "<h3")
      .replace(/<\/h3>\s*<br\/>/g, "</h3>")
      .replace(
        /<br\/>\s*<div class="ai-response-bullet">/g,
        '<div class="ai-response-bullet">'
      );

    return formatted;
  };

  const handleSearch = async e => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    const currentQuestion = searchQuery.trim();
    setSearchQuery("");
    setIsSearching(true);

    const userMessage = {
      type: "user",
      content: currentQuestion,
      timestamp: new Date()
    };

    setConversationHistory(prev => [...prev, userMessage]);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/partners/ai-magic-report/ask`,
        {
          question: currentQuestion,
          chatId: currentChatId
        }
      );

      if (response.data.success) {
        const aiMessage = {
          type: "ai",
          content: response.data.data.answer,
          timestamp: new Date(),
          tableData: response.data.data.tableData || null
        };

        setConversationHistory(prev => [...prev, aiMessage]);

        if (
          response.data.data.followUpQuestions &&
          response.data.data.followUpQuestions.length > 0
        ) {
          setCurrentFollowUpQuestions(response.data.data.followUpQuestions);
        }

        if (response.data.data.chatId) {
          setCurrentChatId(response.data.data.chatId);
        }

        await loadChatHistory();
      } else {
        const errorContent =
          response.data.message ===
          "Sorry, we cannot answer this question as it may be a breach of security"
            ? "Sorry, we cannot answer this question as it may be a breach of security"
            : "Sorry, I encountered an error while processing your question. Please try again in a few moments.";

        const errorMessage = {
          type: "ai",
          content: errorContent,
          timestamp: new Date()
        };
        setConversationHistory(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error("Error:", error);
      let errorContent =
        "Sorry, I encountered an error while processing your question. Please try again.";

      if (error.response) {
        if (error.response.status === 502 || error.response.status === 503) {
          errorContent =
            "The AI service is temporarily unavailable. Please try again in a few moments.";
        } else if (error.response.status === 429) {
          errorContent =
            "Too many requests. Please wait a moment and try again.";
        }
      } else if (error.code === "NETWORK_ERROR") {
        errorContent =
          "Network connection issue. Please check your connection and try again.";
      }

      const errorMessage = {
        type: "ai",
        content: errorContent,
        timestamp: new Date()
      };
      setConversationHistory(prev => [...prev, errorMessage]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSuggestedQuestion = question => {
    setSearchQuery(question);
  };

  const initialSuggestedQuestions = [
    "Show me my top performing programs this quarter",
    "Which clients generated the highest revenue?",
    "What's my total revenue last month?"
  ];

  return (
    <div className="aimagic-modal-cont">
      <div className="aimagic-modal">
        <div className={`aimagic-sidebar ${showSidebar ? "show" : ""}`}>
          <div className="aimagic-sidebar-header">
            <button
              className="aimagic-sidebar-close"
              onClick={() => setShowSidebar(false)}
            >
              ×
            </button>
          </div>

          <div className="aimagic-chat-history">
            <h4>Recent Chats</h4>
            <div className="aimagic-chat-items">
              {chatHistory.length === 0 ? (
                <p className="aimagic-no-history">No chat history yet</p>
              ) : (
                chatHistory.map((chat, index) => (
                  <div
                    key={chat.id || index}
                    className={`aimagic-chat-item ${
                      currentChatId === chat.id ? "active" : ""
                    }`}
                    onClick={() => loadChatFromHistory(chat.id)}
                  >
                    <div className="aimagic-chat-title">{chat.title}</div>
                    <div className="aimagic-chat-date">
                      {new Date(chat.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {showSidebar && (
          <div
            className="aimagic-sidebar-overlay"
            onClick={() => setShowSidebar(false)}
          />
        )}

        <div className="aimagic-modal-header">
          <div className="aimagic-header-left">
            <button
              className="aimagic-menu-btn"
              onClick={() => setShowSidebar(true)}
            >
              ☰
            </button>
            <h3 className="aimagic-modal-title">AI Magic Report</h3>
          </div>
          <div className="aimagic-header-actions">
            <button
              onClick={startNewChat}
              className="aimagic-header-new-chat-btn"
            >
              New Chat
            </button>
            <button onClick={onClose} className="aimagic-close-button">
              ×
            </button>
          </div>
        </div>

        <div className="aimagic-modal-content">
          {isLoadingHistory && (
            <div className="aimagic-loading-overlay">
              <div className="aimagic-loading-spinner"></div>
              <p>Loading chat...</p>
            </div>
          )}

          <div className="aimagic-conversation-area">
            {conversationHistory.length === 0 ? (
              <div className="aimagic-search-container">
                <div className="aimagic-search-header">
                  <h4>Ask me anything about your reports and analytics</h4>
                </div>
              </div>
            ) : (
              <div className="aimagic-conversation">
                {conversationHistory.map((message, index) => (
                  <div key={index} className="aimagic-conversation-entry">
                    <div className={`aimagic-message ${message.type}`}>
                      <div className="aimagic-message-label">
                        <strong>{message.type === "user" ? "Q:" : "A:"}</strong>
                      </div>
                      <div className="aimagic-message-content">
                        {message.type === "ai" ? (
                          <div>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: formatAIResponse(message.content)
                              }}
                            />

                            {message.tableData &&
                              renderTable(message.tableData)}
                          </div>
                        ) : (
                          message.content
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}

            {isSearching && (
              <div className="aimagic-conversation-entry">
                {/* <div className="aimagic-loading"> */}
                <div
                  style={{
                    display: "flex"
                  }}
                >
                  <div className="aimagic-loading-spinner"></div>
                  <p style={{ marginLeft: "10px" }}>Analyzing your data...</p>
                </div>
              </div>
            )}
          </div>

          <div className="aimagic-input-area">
            <form onSubmit={handleSearch} className="aimagic-search-form">
              <div className="aimagic-search-input-container">
                <img
                  src={SearchIcon}
                  alt="Search"
                  className="aimagic-search-icon"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder={
                    conversationHistory.length > 0
                      ? "Ask a follow-up question..."
                      : "e.g., Generate a table of my customers and their revenue"
                  }
                  className="aimagic-search-input"
                  disabled={isSearching}
                />
                <button
                  type="submit"
                  disabled={!searchQuery.trim() || isSearching}
                  className="aimagic-search-button"
                >
                  {isSearching ? "..." : "Ask"}
                </button>
              </div>
            </form>

            <div className="aimagic-examples">
              <p className="aimagic-examples-title">
                {conversationHistory.length === 0
                  ? "Try asking:"
                  : "You might also ask:"}
              </p>
              <div className="aimagic-example-chips">
                {(conversationHistory.length === 0
                  ? initialSuggestedQuestions
                  : currentFollowUpQuestions
                ).map((question, index) => (
                  <button
                    key={index}
                    className="aimagic-example-chip"
                    onClick={() => handleSuggestedQuestion(question)}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIMagicReportModal;
